import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  DollarSign,
  Clock,
  Plug,
  Sparkles,
  ListChecks,
  Calendar,
  Loader2,
  MessageSquare,
} from 'lucide-react'
import {
  fetchPreBuiltTaskBySlug,
  selectPreBuiltTaskBySlug,
  selectPreBuiltTaskDetailStatus,
} from '../../../features/openai/openaiSlice'
import InstallDialog from './InstallDialog'

function formatSavings(cents: number | null | undefined): string {
  if (!cents || cents <= 0) return '—'
  const dollars = cents / 100
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1).replace(/\.0$/, '')}K/yr`
  }
  return `$${dollars.toFixed(0)}/yr`
}

function formatRunTime(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—'
  if (seconds < 90) return `~${seconds}s`
  const m = Math.round(seconds / 60)
  return `~${m}m`
}

function integrationLabel(id: string): string {
  return id
    .replace(/_/g, ' ')
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
}

function formatToolName(name: string): string {
  return name
    .replace(/^mcp__payhelm__/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function PreBuiltTaskDetailView() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const [sp] = useSearchParams()
  const task = useSelector((state: any) => selectPreBuiltTaskBySlug(state, slug || ''))
  const status = useSelector((state: any) => selectPreBuiltTaskDetailStatus(state, slug || ''))
  const [installOpen, setInstallOpen] = useState(false)

  useEffect(() => {
    if (slug) dispatch(fetchPreBuiltTaskBySlug(slug) as any)
  }, [dispatch, slug])

  useEffect(() => {
    if (sp.get('install') === '1' && task) setInstallOpen(true)
  }, [sp, task])

  if (status === 'loading' && !task) {
    return (
      <div id="task-library-body" className="tw-min-h-screen tw-bg-white">
        <div className="tw-px-6 tw-py-8 tw-text-center tw-text-sm tw-text-slate-500">
          <Loader2 className="tw-mx-auto tw-h-5 tw-w-5 tw-animate-spin tw-mb-2" />
          Loading…
        </div>
      </div>
    )
  }

  if (status === 'failed' || (!task && status === 'succeeded')) {
    return (
      <div id="task-library-body" className="tw-min-h-screen tw-bg-white">
        <div className="tw-px-6 tw-py-8 tw-text-center">
          <p className="tw-text-sm tw-text-slate-600 tw-mb-3">This task isn't available.</p>
          <button
            onClick={() => navigate('/task-library?tab=pre-built')}
            className="tw-text-sm tw-text-blue-600 hover:tw-underline"
          >
            Back to Task Library
          </button>
        </div>
      </div>
    )
  }

  if (!task) return null

  const integrations: string[] = task.integrations || []
  const tools: any[] = Array.isArray(task.tools)
    ? [...task.tools].sort((a, b) => (a.execution_order ?? 999) - (b.execution_order ?? 999))
    : []
  const whyAutomate: any[] = Array.isArray(task.why_automate) ? task.why_automate : []

  return (
    <div id="task-library-body" className="tw-min-h-screen tw-bg-white">
      <div className="tw-px-6 tw-py-8">
        <button
          onClick={() => navigate('/task-library?tab=pre-built')}
          className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-slate-600 hover:tw-text-slate-900 tw-mb-4"
        >
          <ArrowLeft className="tw-h-4 tw-w-4" /> Back to Task Library
        </button>

        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-p-6 tw-shadow-sm tw-mb-5">
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-flex-wrap">
            <div className="tw-flex-1 tw-min-w-0">
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 tw-mb-2">
                <Sparkles className="tw-h-3.5 tw-w-3.5" />
                <span>Task Library</span>
              </div>
              <h1 className="tw-text-2xl tw-font-semibold tw-text-slate-900 tw-mb-2">{task.name}</h1>
              {task.summary && (
                <p className="tw-text-sm tw-text-slate-600 tw-leading-relaxed">{task.summary}</p>
              )}
            </div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
              <Link
                to={`/ai?preBuiltTaskId=${encodeURIComponent(task.id)}`}
                state={{ autoStartPreBuiltTask: true }}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-bg-white tw-text-blue-700 tw-border tw-border-blue-200 hover:tw-bg-blue-50 tw-no-underline"
              >
                <MessageSquare className="tw-h-4 tw-w-4" />
                Chat Now
              </Link>
              <button
                type="button"
                onClick={() => setInstallOpen(true)}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-shadow-sm"
              >
                <Sparkles className="tw-h-4 tw-w-4" />
                Add to my tasks
              </button>
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-3 tw-mt-5 tw-pt-5 tw-border-t tw-border-slate-100">
            <div className="tw-flex tw-items-start tw-gap-2">
              <DollarSign className="tw-h-4 tw-w-4 tw-text-emerald-600 tw-mt-0.5" />
              <div>
                <div className="tw-text-xs tw-text-slate-500">Estimated savings</div>
                <div className="tw-text-sm tw-font-semibold tw-text-slate-900">
                  {formatSavings(task.estimated_annual_savings_cents)}
                </div>
              </div>
            </div>
            <div className="tw-flex tw-items-start tw-gap-2">
              <Clock className="tw-h-4 tw-w-4 tw-text-slate-500 tw-mt-0.5" />
              <div>
                <div className="tw-text-xs tw-text-slate-500">Time to run</div>
                <div className="tw-text-sm tw-font-semibold tw-text-slate-900">
                  {formatRunTime(task.estimated_run_seconds)}
                </div>
              </div>
            </div>
            <div className="tw-flex tw-items-start tw-gap-2">
              <Calendar className="tw-h-4 tw-w-4 tw-text-slate-500 tw-mt-0.5" />
              <div>
                <div className="tw-text-xs tw-text-slate-500">Default cadence</div>
                <div className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-capitalize">
                  {task.schedule_type || '—'}
                </div>
              </div>
            </div>
            <div className="tw-flex tw-items-start tw-gap-2">
              <Plug className="tw-h-4 tw-w-4 tw-text-slate-500 tw-mt-0.5" />
              <div>
                <div className="tw-text-xs tw-text-slate-500">Integrations</div>
                <div className="tw-text-sm tw-font-semibold tw-text-slate-900">
                  {integrations.length === 0 ? '—' : integrations.map(integrationLabel).join(', ')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {whyAutomate.length > 0 && (
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-p-6 tw-shadow-sm tw-mb-5">
            <h2 className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-uppercase tw-tracking-wide tw-mb-3">
              Why automate this
            </h2>
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
              {whyAutomate.slice(0, 6).map((tile: any, i: number) => (
                <div key={i} className="tw-rounded-lg tw-border tw-border-slate-200 tw-p-3 tw-bg-slate-50">
                  <div className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-mb-1">
                    {tile.label || tile.title || `Reason ${i + 1}`}
                  </div>
                  <div className="tw-text-sm tw-text-slate-800 tw-leading-snug">
                    {tile.value || tile.description || tile.body || ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {task.description && (
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-p-6 tw-shadow-sm tw-mb-5">
            <h2 className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-uppercase tw-tracking-wide tw-mb-3">
              What this task does
            </h2>
            <p className="tw-text-sm tw-text-slate-700 tw-leading-relaxed tw-whitespace-pre-wrap">
              {task.description}
            </p>
          </div>
        )}

        {tools.length > 0 && (
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-p-6 tw-shadow-sm tw-mb-5">
            <h2 className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-uppercase tw-tracking-wide tw-mb-3">
              <ListChecks className="tw-inline tw-h-4 tw-w-4 tw-mr-1.5 tw-align-text-bottom" />
              How it runs
            </h2>
            <ol className="tw-space-y-2">
              {tools.map((t: any, i: number) => (
                <li key={i} className="tw-flex tw-gap-3">
                  <span className="tw-flex-shrink-0 tw-inline-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-rounded-full tw-bg-slate-100 tw-text-slate-600 tw-text-xs tw-font-semibold">
                    {i + 1}
                  </span>
                  <div className="tw-flex-1 tw-min-w-0 tw-pt-0.5">
                    <div className="tw-text-sm tw-font-medium tw-text-slate-900">
                      {t.display_name || formatToolName(t.tool_name || '')}
                    </div>
                    {t.display_description && (
                      <div className="tw-text-xs tw-text-slate-600 tw-mt-0.5">{t.display_description}</div>
                    )}
                    {!t.display_description && t.tool_name && (
                      <div className="tw-text-xs tw-text-slate-500 tw-font-mono tw-mt-0.5">{t.tool_name}</div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {task.example_output && (
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-p-6 tw-shadow-sm tw-mb-5">
            <h2 className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-uppercase tw-tracking-wide tw-mb-3">
              Example output
            </h2>
            <pre className="tw-text-xs tw-text-slate-700 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-md tw-p-3 tw-overflow-x-auto tw-whitespace-pre-wrap tw-font-mono">
              {task.example_output}
            </pre>
          </div>
        )}

      </div>

      <InstallDialog task={task} open={installOpen} onClose={() => setInstallOpen(false)} />
    </div>
  )
}
